import { useEffect, useRef } from 'react';
import { useSectionMetrics } from '../motion/scroll';
import { range } from '../motion/tokens';
import { useLocale } from '../i18n/locale';
import { sectionId } from '../content/site';
import { HOME } from './content';
import { Lines } from './Lines';

/**
 * "Many threads, one line." The fragmented purchase is drawn as threads between the company and
 * eight actors; scrolling pulls them into one line to delivery in Colombia under one counterparty.
 * Landscape frames run left → right; portrait frames (tablet portrait, phone) run top → bottom.
 */
type Point = [number, number];
const SCATTER_WIDE: Point[] = [[0.3, 0.18], [0.52, 0.1], [0.78, 0.2], [0.4, 0.44], [0.66, 0.5], [0.28, 0.78], [0.55, 0.88], [0.84, 0.72]];
const SCATTER_TALL: Point[] = [[0.2, 0.2], [0.66, 0.15], [0.86, 0.32], [0.34, 0.4], [0.72, 0.54], [0.18, 0.64], [0.56, 0.76], [0.86, 0.84]];
const LINKS: [number, number][] = [[0, 3], [1, 4], [2, 4], [3, 5], [4, 6], [5, 6], [6, 7], [1, 2], [0, 5], [3, 7]];
const ease = (t: number) => t * t * (3 - 2 * t);
const mix = (a: Point, b: Point, t: number): Point => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];

export function Threads() {
  const { locale } = useLocale(); const THREADS = HOME[locale].threads;
  const ref = useRef<HTMLElement>(null);
  const figure = useRef<HTMLDivElement>(null);
  const svg = useRef<SVGSVGElement>(null);
  const size = useRef({ w: 1, h: 1 });
  const before = useRef<HTMLDivElement>(null);
  const after = useRef<HTMLDivElement>(null);
  const last = useRef(-1);

  useEffect(() => {
    const element = figure.current; if (!element) return;
    const observer = new ResizeObserver(() => { size.current = { w: element.clientWidth, h: element.clientHeight }; svg.current?.setAttribute('viewBox', `0 0 ${size.current.w} ${size.current.h}`); draw(last.current < 0 ? 0 : last.current, true); });
    observer.observe(element); return () => observer.disconnect();
  }, []);

  function draw(track: number, force = false) {
    if (!force && Math.abs(track - last.current) < 0.0005) return; last.current = track;
    const root = svg.current; const host = figure.current; if (!root || !host) return;
    const { w, h } = size.current; const tall = h > w * 0.9; host.dataset.tall = String(tall);
    const company: Point = tall ? [0.5, 0.04] : [0.05, 0.5]; const delivery: Point = tall ? [0.5, 0.96] : [0.95, 0.5];
    const scatter = tall ? SCATTER_TALL : SCATTER_WIDE;
    const line = scatter.map((_, i): Point => (tall ? [0.5, 0.14 + (i / 7) * 0.72] : [0.22 + (i / 7) * 0.56, 0.5]));
    const collapse = ease(range(track, 0.42, 0.72)); const reveal = range(track, 0.04, 0.3); const draw = range(track, 0.6, 0.84);
    const px = ([x, y]: Point): Point => [x * w, y * h];
    const actors = scatter.map((p, i) => px(mix(p, line[i], collapse)));
    const c = px(company); const d = px(delivery);
    const curve = (a: Point, b: Point, bend: number) => {
      const mx = (a[0] + b[0]) / 2; const my = (a[1] + b[1]) / 2; const nx = -(b[1] - a[1]); const ny = b[0] - a[0];
      return `M${a[0].toFixed(1)} ${a[1].toFixed(1)} Q${(mx + nx * bend).toFixed(1)} ${(my + ny * bend).toFixed(1)} ${b[0].toFixed(1)} ${b[1].toFixed(1)}`;
    };
    const threads = root.querySelectorAll<SVGPathElement>('[data-thread]');
    threads.forEach((path, i) => {
      const fromCompany = i < actors.length; const [a, b] = fromCompany ? [c, actors[i]] : [actors[LINKS[i - actors.length][0]], actors[LINKS[i - actors.length][1]]];
      const bend = (fromCompany ? 0.18 : 0.32) * (i % 2 ? 1 : -1) * (1 - collapse);
      path.setAttribute('d', curve(a, b, bend));
      // Threads appear one after another: the coordination load accumulates.
      const show = range(reveal, i / threads.length, (i + 1) / threads.length);
      path.style.opacity = (show * (1 - range(collapse, 0.4, 1)) * (fromCompany ? 0.55 : 0.32)).toFixed(3);
    });
    const main = root.querySelector<SVGPathElement>('[data-main]');
    if (main) { main.setAttribute('d', `M${c[0]} ${c[1]} L${d[0]} ${d[1]}`); const length = Math.hypot(d[0] - c[0], d[1] - c[1]); main.style.strokeDasharray = `${length}`; main.style.strokeDashoffset = `${length * (1 - draw)}`; }
    const bracket = root.querySelector<SVGPathElement>('[data-bracket]');
    if (bracket) {
      const first = px(line[0]); const lastPoint = px(line[line.length - 1]); const gap = tall ? Math.min(w * 0.2, 110) : Math.min(h * 0.17, 96); const tick = 10;
      bracket.setAttribute('d', tall
        ? `M${first[0] - gap + tick} ${first[1]} L${first[0] - gap} ${first[1]} L${first[0] - gap} ${lastPoint[1]} L${first[0] - gap + tick} ${lastPoint[1]}`
        : `M${first[0]} ${first[1] - gap + tick} L${first[0]} ${first[1] - gap} L${lastPoint[0]} ${first[1] - gap} L${lastPoint[0]} ${first[1] - gap + tick}`);
      bracket.style.opacity = range(draw, 0.5, 1).toFixed(3);
      const label = host.querySelector<HTMLElement>('[data-counterpart]');
      if (label) {
        label.style.opacity = range(draw, 0.55, 1).toFixed(3);
        label.style.transform = tall ? `translate(${first[0] - gap - 14}px, ${(first[1] + lastPoint[1]) / 2}px) translate(-100%, -50%)` : `translate(${(first[0] + lastPoint[0]) / 2}px, ${first[1] - gap - 12}px) translate(-50%, -100%)`;
      }
    }
    host.querySelectorAll<HTMLElement>('[data-actor]').forEach((node, i) => {
      const [x, y] = actors[i]; const side = tall ? 1 : (i % 2 ? 1 : -1);
      node.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`;
      node.style.opacity = (0.25 + 0.75 * range(reveal, i / 8 - 0.05, i / 8 + 0.1)).toFixed(3);
      node.dataset.aligned = String(collapse > 0.98);
      const label = node.firstElementChild as HTMLElement | null;
      if (label) label.dataset.side = collapse > 0.5 ? (tall ? 'right' : side > 0 ? 'below' : 'above') : 'right';
    });
    host.querySelector<HTMLElement>('[data-company]')?.style.setProperty('transform', `translate(${c[0]}px, ${c[1]}px)`);
    const end = host.querySelector<HTMLElement>('[data-delivery]');
    if (end) { end.style.transform = `translate(${d[0]}px, ${d[1]}px)`; end.style.opacity = (0.3 + 0.7 * draw).toFixed(3); }
    if (before.current) { const v = 1 - range(track, 0.4, 0.5); before.current.style.opacity = v.toFixed(3); before.current.style.visibility = v < 0.02 ? 'hidden' : 'visible'; }
    if (after.current) { const v = range(track, 0.52, 0.62); after.current.style.opacity = v.toFixed(3); after.current.style.visibility = v < 0.02 ? 'hidden' : 'visible'; }
  }

  useSectionMetrics(ref, ({ track, visible }) => { if (visible) draw(track); });

  return (
    <section ref={ref} id={sectionId('problem', locale)} className="threads" data-tone="light" aria-labelledby="threads-title">
      <div className="threads__sticky">
        <div className="threads__inner wrap">
          <div className="threads__copy">
            <div ref={before} className="threads__state">
              <h2 id="threads-title" className="section-title"><Lines lines={THREADS.before.title} /></h2>
              <p className="section-body">{THREADS.before.body}</p>
            </div>
            <div ref={after} className="threads__state threads__state--after">
              <h2 className="section-title"><Lines lines={THREADS.after.title} /></h2>
              <p className="section-body">{THREADS.after.body}</p>
            </div>
          </div>
          <div ref={figure} className="threads__figure" aria-hidden="true">
            <svg ref={svg} className="threads__svg" preserveAspectRatio="none">
              {[...Array(8).keys(), ...LINKS].map((_, i) => <path key={i} data-thread="" className={`threads__thread${i % 3 === 2 ? ' threads__thread--dashed' : ''}`} />)}
              <path data-main="" className="threads__main" />
              <path data-bracket="" className="threads__bracket" />
            </svg>
            <span data-company="" className="threads__end threads__end--company"><span className="threads__end-label">{THREADS.company}</span></span>
            <span data-delivery="" className="threads__end threads__end--delivery"><span className="threads__end-label">{THREADS.delivery}</span></span>
            {THREADS.actors.map(actor => <span key={actor} data-actor="" className="threads__actor"><span className="threads__actor-label">{actor}</span></span>)}
            <span data-counterpart="" className="threads__counterpart">COLESCOMEX</span>
          </div>
          <ul className="visually-hidden" aria-label={THREADS.actorsLabel}>{THREADS.actors.map(actor => <li key={actor}>{actor}</li>)}</ul>
        </div>
      </div>
    </section>
  );
}
